import { useInfiniteQuery } from '@tanstack/react-query';
import {
  RESTAURANT_REFERRALS_PAGE_SIZE,
  RestaurantReferralStatus,
  getRestaurantReferrals,
  queryKeys,
} from '@vgrubs/components';

type Filters = {
  status: RestaurantReferralStatus | null;
  /** Already debounced and trimmed. */
  query: string;
};

/**
 * The program's referrals a page at a time, newest first, as the server sends
 * them.
 *
 * The four totals ride on every page and describe the whole program whatever
 * the filters, so they are read off the first page and never summed.
 */
export const useReferralsList = ({ status, query }: Filters) => {
  const list = useInfiniteQuery({
    queryKey: queryKeys.restaurantReferrals.list(status, query),
    queryFn: async ({ pageParam }) => {
      const { data } = await getRestaurantReferrals({
        page: pageParam,
        limit: RESTAURANT_REFERRALS_PAGE_SIZE,
        ...(status ? { status } : {}),
        ...(query ? { query } : {}),
      });
      return data.data;
    },
    initialPageParam: 1,
    /* There is no count for the filtered list (the totals are the program's),
       so a short page is the only sign of the end. */
    getNextPageParam: (lastPage, pages) =>
      lastPage.items.length < RESTAURANT_REFERRALS_PAGE_SIZE
        ? undefined
        : pages.length + 1,
    // Keeps the cards and rows up while another tab's list loads.
    placeholderData: (previous) => previous,
  });

  const first = list.data?.pages[0];

  return {
    referrals: (list.data?.pages ?? []).flatMap((page) => page.items),
    totals: first
      ? {
          totalCount: first.totalCount,
          inProgressCount: first.inProgressCount,
          rewardedCount: first.rewardedCount,
          totalPaidOut: first.totalPaidOut,
        }
      : null,
    isLoading: list.isLoading,
    /** Also true while another filter's list replaces the one on screen. */
    isFetching: list.isFetching && !list.isFetchingNextPage,
    isFetchingNextPage: list.isFetchingNextPage,
    hasNextPage: list.hasNextPage,
    fetchNextPage: list.fetchNextPage,
  };
};
